import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { onboardingQuestionnaireActions } from '../../../../../../../store';
import { readInsuranceExtractionStorage } from './utils';
import useInsuranceCardExtraction from './useInsuranceCardExtraction';
import { INSURANCE_TYPES } from '../../../../../../../constants';

import type { AppDispatch } from '../../../../../../../store';
import type { InsuranceCardExtractionManagerState } from './types';
import type { InsuranceType } from '../../../../../../../utils/types';

export default function useInsuranceCardExtractionManager(
  extractionEnabled: boolean,
): InsuranceCardExtractionManagerState {
  const dispatch = useDispatch<AppDispatch>();
  const { onboardingQuestionnaireId } = useParams<'onboardingQuestionnaireId'>();

  useEffect(() => {
    if (!onboardingQuestionnaireId) {
      return;
    }
    dispatch(
      onboardingQuestionnaireActions.hydrateInsuranceExtraction({
        map: readInsuranceExtractionStorage(onboardingQuestionnaireId),
      }),
    );
  }, [dispatch, onboardingQuestionnaireId]);

  const primary = useInsuranceCardExtraction(INSURANCE_TYPES.PRIMARY, extractionEnabled);
  const secondary = useInsuranceCardExtraction(INSURANCE_TYPES.SECONDARY, extractionEnabled);

  // Only one overwrite prompt is shown at a time — the primary card takes precedence when both
  // sections happen to be waiting on confirmation.
  const pending = primary.needsOverwriteConfirmation ? primary : secondary;
  let overwriteConfirmationInsuranceType: InsuranceType | null = null;
  if (primary.needsOverwriteConfirmation) {
    overwriteConfirmationInsuranceType = INSURANCE_TYPES.PRIMARY;
  } else if (secondary.needsOverwriteConfirmation) {
    overwriteConfirmationInsuranceType = INSURANCE_TYPES.SECONDARY;
  }

  return {
    overwriteConfirmationInsuranceType,
    confirmOverwrite: pending.confirmOverwrite,
    cancelOverwrite: pending.cancelOverwrite,
  };
}
